/**
 * Knowledge — phase 2 §7.2.
 *
 * "**Knowledge and Projects split what is currently one list.** Today
 * `Company Handbook` and `Acme Corp` are siblings in a single space tree, which
 * collides two mental models. Separating them is entirely a view-layer change —
 * the `space` table is untouched."
 *
 * This is the first half of that split. It asks for `scope=knowledge`, which the
 * API answers by excluding every space an organization or a project reaches,
 * ancestors included — so a client's shared space cannot appear here however
 * deep it sits. Nothing about the tree changed; a different question was asked
 * of it.
 *
 * There is no create button. §4.2 is explicit that hand-made spaces are the
 * exception now — "almost nobody creates a space directly", and the ones that
 * remain are admin work — so creating one lives with the rest of the raw tree,
 * behind Schema.
 */

import { Link } from '@tanstack/react-router';
import { useSpaces } from '../../api/queries.ts';
import { Badge } from '../../components/ui/badge.tsx';
import { EmptyState, ErrorState, Loading } from '../../components/ui/feedback.tsx';
import { Card, PageHeader } from '../../components/ui/layout.tsx';

export function KnowledgeListPage() {
  const spaces = useSpaces('document.view', 'knowledge');
  const rows = spaces.data?.spaces ?? [];

  return (
    <>
      <PageHeader
        title="Knowledge"
        description="The internal spaces that are nobody's client and nobody's project — handbooks, playbooks, references. Client work lives under Clients and Projects; this is the material that outlives an engagement."
      />

      {spaces.isError ? <ErrorState error={spaces.error} /> : null}
      {spaces.isPending ? <Loading label="Resolving what you can reach…" /> : null}

      {!spaces.isPending && rows.length === 0 ? (
        <EmptyState
          title="No knowledge space you can reach"
          description="These spaces are granted like any other. If you expected one here, the grant reaching it is what is missing — not the space."
        />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((space) => (
          <Link
            key={space.id}
            to="/spaces/$spaceId"
            params={{ spaceId: space.id }}
            className="no-underline"
          >
            <Card className="h-full transition-colors hover:bg-secondary">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-primary">{space.name}</span>
                {space.isTemplate ? <Badge tone="gray">template</Badge> : null}
              </div>
              <p className="mt-2 text-sm text-tertiary">
                {space.parentSpaceId ? 'A space inside another' : 'A root of its own'}
              </p>
            </Card>
          </Link>
        ))}
      </div>
    </>
  );
}
